import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const PREFIX_FORMATS = {
  NEW: 'new',
  OLD: 'old'
};

const COLOR_MODES = {
  DEFAULT: 'default',
  MANUAL: 'manual'
};

const DEFAULT_COLORS_OLD = {
  font: '#49300a',
  shadow: '#c17905',
  outline: '#ad6c05',
  background: '#ffa007',
  backgroundLight: '#ffb43d'
};

const DEFAULT_COLORS_NEW = {
  font: '#2c1e00',
  shadowTop: '#926500',
  shadowBottom: '#885e00',
  outline: '#1e1e1e',
  innerOutlineTop: '#f1a600',
  innerOutlineBottom: '#cd8a00',
  backgroundTop: '#b67e00',
  backgroundBottom: '#ac7600'
};

// Character mappings for different formats
const CHAR_MAPPINGS = {
  new: {
    'A': 'A_Symbol.png',
    'B': 'B_Symbol.png',
    'C': 'C_Symbol.png',
    'D': 'D_Symbol.png',
    'E': 'E_Symbol.png',
    'F': 'F_Symbol.png',
    'G': 'G_Symbol.png',
    'H': 'H_Symbol.png',
    'I': 'I_Symbol.png',
    'J': 'J_Symbol.png',
    'K': 'K_Symbol.png',
    'L': 'L_Symbol.png',
    'M': 'M_Symbol.png',
    'N': 'N_Symbol.png',
    'O': 'O_Symbol.png',
    'P': 'P_Symbol.png',
    'Q': 'Q_Symbol.png',
    'R': 'R_Symbol.png',
    'S': 'S_Symbol.png',
    'T': 'T_Symbol.png',
    'U': 'U_Symbol.png',
    'V': 'V_Symbol.png',
    'W': 'W_Symbol.png',
    'X': 'X_Symbol.png',
    'Y': 'Y_Symbol.png',
    'Z': 'Z_Symbol.png',
    'a': 'a_lc_Symbol.png',
    'b': 'b_lc_Symbol.png',
    'c': 'c_lc_Symbol.png',
    'd': 'd_lc_Symbol.png',
    'e': 'e_lc_Symbol.png',
    'f': 'f_lc_Symbol.png',
    'g': 'g_lc_Symbol.png',
    'h': 'h_lc_Symbol.png',
    'i': 'i_lc_Symbol.png',
    'j': 'j_lc_Symbol.png',
    'k': 'k_lc_Symbol.png',
    'l': 'l_lc_Symbol.png',
    'm': 'm_lc_Symbol.png',
    'n': 'n_lc_Symbol.png',
    'o': 'o_lc_Symbol.png',
    'p': 'p_lc_Symbol.png',
    'q': 'q_lc_Symbol.png',
    'r': 'r_lc_Symbol.png',
    's': 's_lc_Symbol.png',
    't': 't_lc_Symbol.png',
    'u': 'u_lc_Symbol.png',
    'v': 'v_lc_Symbol.png',
    'w': 'w_lc_Symbol.png',
    'x': 'x_lc_Symbol.png',
    'y': 'y_lc_Symbol.png',
    'z': 'z_lc_Symbol.png',
    '0': '0_Symbol.png',
    '1': '1_Symbol.png',
    '2': '2_Symbol.png',
    '3': '3_Symbol.png',
    '4': '4_Symbol.png',
    '5': '5_Symbol.png',
    '6': '6_Symbol.png',
    '7': '7_Symbol.png',
    '8': '8_Symbol.png',
    '9': '9_Symbol.png',
    ' ': 'Space_Symbol.png',
    '-': 'Dash_Symbol.png',
    '.': 'Period_Symbol.png',
    '!': 'Exclamation_Symbol.png',
    '?': 'Question_Symbol.png',
    ':': 'Colon_Symbol.png',
    '#': 'Hashtag_Symbol.png',
    '%': 'Percent_Symbol.png',
    '+': 'Plus_Symbol.png',
    '/': 'Slash_Symbol.png',
    '\\': 'Backslash_Symbol.png',
    '[': 'LeftBracket_Symbol.png',
    ']': 'RightBracket_Symbol.png',
    '(': 'RoundBracketLeft_Symbol.png',
    ')': 'RoundBracketRight_Symbol.png',
    '{': 'LeftCurlyBracket_Symbol.png',
    '}': 'RightCurlyBracket_Symbol.png',
    '"': 'Doublequote_Symbol.png',
    "'": 'Quote_Symbol.png',
    '|': 'Stick_Symbol.png'
  },
  old: {
    'A': 'A_Symbol.png',
    'B': 'B_Symbol.png',
    'C': 'C_Symbol.png',
    'D': 'D_Symbol.png',
    'E': 'E_Symbol.png',
    'F': 'F_Symbol.png',
    'G': 'G_Symbol.png',
    'H': 'H_Symbol.png',
    'I': 'I_Symbol.png',
    'J': 'J_Symbol.png',
    'K': 'K_Symbol.png',
    'L': 'L_Symbol.png',
    'M': 'M_Symbol.png',
    'N': 'N_Symbol.png',
    'O': 'O_Symbol.png',
    'P': 'P_Symbol.png',
    'Q': 'Q_Symbol.png',
    'R': 'R_Symbol.png',
    'S': 'S_Symbol.png',
    'T': 'T_Symbol.png',
    'U': 'U_Symbol.png',
    'V': 'V_Symbol.png',
    'W': 'W_Symbol.png',
    'X': 'X_Symbol.png',
    'Y': 'Y_Symbol.png',
    'Z': 'Z_Symbol.png',
    '0': '0_Symbol.png',
    '1': '1_Symbol.png',
    '2': '2_Symbol.png',
    '3': '3_Symbol.png',
    '4': '4_Symbol.png',
    '5': '5_Symbol.png',
    '6': '6_Symbol.png',
    '7': '7_Symbol.png',
    '8': '8_Symbol.png',
    '9': '9_Symbol.png',
    ' ': 'Space_Symbol.png',
    '-': 'Dash_Symbol.png',
    '.': 'Period_Symbol.png'
  }
};

// Get allowed characters for each format
const getAllowedChars = (format) => {
  if (format === PREFIX_FORMATS.NEW) {
    return /[a-zA-Z0-9 \-\.!?:#%+\/\\\[\](){}\"'|]/g;
  }
  return /[A-Z0-9 \-\.]/g;
};

// Image loading utility function
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => {
      console.error(`Failed to load image: ${src}`);
      reject(new Error(`Failed to load image: ${src}`));
    };
    img.src = src;
  });
};

const Slider = ({ label, value, min, max, onChange, isOrange = false }) => (
  <div className="mb-4">
    <label className="block text-[#d9d9d9] text-sm mb-2 font-semibold">
      {label} ({value}{label.includes('Hue') ? '°' : '%'})
    </label>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className={`w-full h-2 bg-[#2a2a2a] rounded-lg appearance-none cursor-pointer ${isOrange ? 'slider-orange' : 'slider'}`}
      style={{
        background: `linear-gradient(to right, ${isOrange ? '#ff8c00' : '#cf871c'} 0%, ${isOrange ? '#ff8c00' : '#cf871c'} ${((value - min) / (max - min)) * 100}%, #2a2a2a ${((value - min) / (max - min)) * 100}%, #2a2a2a 100%)`
      }}
    />
  </div>
);

const ColorPicker = ({ label, value, onChange }) => (
  <div className="mb-3">
    <label className="block text-[#d9d9d9] text-xs mb-1.5 font-semibold">
      {label}
    </label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 bg-[#242424] border border-[#2a2a2a] rounded cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-[#242424] border border-[#2a2a2a] rounded px-2 py-1 text-white text-xs"
        placeholder="#000000"
      />
    </div>
  </div>
);

const ColorPickerSimple = ({ label, value, onChange }) => (
  <div>
    <label className="block text-[#d9d9d9] text-xs mb-1.5 font-semibold">
      {label}
    </label>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 bg-[#242424] border border-[#2a2a2a] rounded cursor-pointer"
    />
  </div>
);

const EXAMPLES = [
  { label: 'ADMIN', hue: 158, saturation: 100, lightness: 100, format: PREFIX_FORMATS.NEW },
  { label: 'ELITE', hue: 45, saturation: 100, lightness: 100, format: PREFIX_FORMATS.NEW },
  { label: 'Member', hue: 0, saturation: 0, lightness: 100, format: PREFIX_FORMATS.NEW },
];

const NitroSetupsPrefixCreator = () => {
  const [text, setText] = useState('');
  const [backgroundType, setBackgroundType] = useState('straight');
  const [prefixFormat, setPrefixFormat] = useState(PREFIX_FORMATS.NEW);
  const [colorMode, setColorMode] = useState(COLOR_MODES.DEFAULT);
  const [showSymbolsModal, setShowSymbolsModal] = useState(false);
  const [configFormat, setConfigFormat] = useState('vanilla');
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  
  // Padding controls - updated defaults and mins
  const [paddingLeft, setPaddingLeft] = useState(prefixFormat === PREFIX_FORMATS.OLD ? 2 : (backgroundType === 'rounded' ? 4 : 4));
  const [paddingRight, setPaddingRight] = useState(prefixFormat === PREFIX_FORMATS.OLD ? 2 : (backgroundType === 'rounded' ? 3 : 3));
  
  // Default mode colors
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  
  // Manual mode colors - Old Format
  const [fontColor, setFontColor] = useState(DEFAULT_COLORS_OLD.font);
  const [shadowColor, setShadowColor] = useState(DEFAULT_COLORS_OLD.shadow);
  const [outlineColor, setOutlineColor] = useState(DEFAULT_COLORS_OLD.outline);
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_COLORS_OLD.background);
  const [backgroundLightColor, setBackgroundLightColor] = useState(DEFAULT_COLORS_OLD.backgroundLight);
  
  // Manual mode colors - New Format
  const [fontColorNew, setFontColorNew] = useState(DEFAULT_COLORS_NEW.font);
  const [shadowTopNew, setShadowTopNew] = useState(DEFAULT_COLORS_NEW.shadowTop);
  const [shadowBottomNew, setShadowBottomNew] = useState(DEFAULT_COLORS_NEW.shadowBottom);
  const [outlineColorNew, setOutlineColorNew] = useState(DEFAULT_COLORS_NEW.outline);
  const [innerOutlineTopNew, setInnerOutlineTopNew] = useState(DEFAULT_COLORS_NEW.innerOutlineTop);
  const [innerOutlineBottomNew, setInnerOutlineBottomNew] = useState(DEFAULT_COLORS_NEW.innerOutlineBottom);
  const [backgroundTopNew, setBackgroundTopNew] = useState(DEFAULT_COLORS_NEW.backgroundTop);
  const [backgroundBottomNew, setBackgroundBottomNew] = useState(DEFAULT_COLORS_NEW.backgroundBottom);
  
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [lettersLoaded, setLettersLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [jsonCode, setJsonCode] = useState(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [examplePreviews, setExamplePreviews] = useState({});
  
  const canvasRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const letterImagesRef = useRef({});
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowConfigDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update padding defaults when format changes
  useEffect(() => {
    if (prefixFormat === PREFIX_FORMATS.OLD) {
      setPaddingLeft(2);
      setPaddingRight(2);
    } else if (prefixFormat === PREFIX_FORMATS.NEW) {
      if (backgroundType === 'rounded') {
        setPaddingLeft(4);
        setPaddingRight(3);
      } else {
        setPaddingLeft(4);
        setPaddingRight(3);
      }
    }
  }, [prefixFormat, backgroundType]);

  const resetPadding = () => {
    if (prefixFormat === PREFIX_FORMATS.OLD) {
      setPaddingLeft(2);
      setPaddingRight(2);
    } else if (prefixFormat === PREFIX_FORMATS.NEW) {
      if (backgroundType === 'rounded') {
        setPaddingLeft(4);
        setPaddingRight(3);
      } else {
        setPaddingLeft(4);
        setPaddingRight(3);
      }
    }
  };

  // Get minimum padding based on format and background type
  const getMinPadding = () => {
    if (prefixFormat === PREFIX_FORMATS.NEW && backgroundType === 'rounded') {
      return 2;
    }
    return 0;
  };

  const handleTextChange = (value) => {
    const processedValue = prefixFormat === PREFIX_FORMATS.OLD ? value.toUpperCase() : value;
    const allowedChars = getAllowedChars(prefixFormat);
    const filtered = processedValue.match(allowedChars);
    setText(filtered ? filtered.join('').slice(0, 14) : '');
  };

  const getPlaceholder = () => {
    if (prefixFormat === PREFIX_FORMATS.NEW) {
      return "a-z, A-Z, 0-9, spaces, and symbols";
    }
    return "A-Z, 0-9, spaces, dash (-) and period (.)";
  };

  const handleGenerateJson = (format = configFormat) => {
    const randomPUA = String.fromCodePoint(0xE000 + Math.floor(Math.random() * (0xF8FF - 0xE000 + 1)));
    const height = prefixFormat === PREFIX_FORMATS.NEW ? 8 : 7;
    const ascent = 7;
    const sanitizedText = text.toLowerCase().replace(/\s+/g, '-');
    const fileName = `rank-prefix-${sanitizedText}.png`;
    
    let config;
    
    if (format === 'vanilla') {
      // Vanilla Minecraft format
      config = {
        providers: [
          {
            type: "bitmap",
            file: `minecraft:guis/prefixes/${fileName}`,
            ascent: ascent,
            height: height,
            chars: [randomPUA]
          }
        ]
      };
    } else if (format === 'nexo') {
      // Nexo format
      config = {
        [`prefix_${sanitizedText}`]: {
          texture: `guis/prefixes/${fileName}`,
          ascent: ascent,
          height: height,
          permission: `nexo.glyph.prefix_${sanitizedText}`,
          placeholders: [
            `<prefix:${sanitizedText}>`,
            `:prefix_${sanitizedText}:`
          ]
        }
      };
    } else if (format === 'itemsadder') {
      // ItemsAdder format
      config = {
        info: {
          namespace: "custom_prefixes"
        },
        font_images: {
          [`prefix_${sanitizedText}`]: {
            permission: "ranks.prefix",
            show_in_gui: true,
            suggest_in_command: false,
            path: `font/prefixes/${fileName}`,
            scale_ratio: height,
            y_position: ascent
          }
        }
      };
    }
    
    const formattedConfig = format === 'itemsadder' ? 
      JSON.stringify(config, null, 2).replace(/^/gm, '  ').trim() : 
      JSON.stringify(config, null, 4);
    
    setJsonCode(formattedConfig);
    setShowJsonModal(true);
  };

  // Load background and letters
  useEffect(() => {
    const loadImages = async () => {
      setBackgroundLoaded(false);
      setLettersLoaded(false);
      setError(null);
      
      try {
        const formatFolder = prefixFormat === PREFIX_FORMATS.NEW ? 'new_prefix_format' : 'old_prefix_format';
        const bgImagePath = `/${formatFolder}/${backgroundType.charAt(0).toUpperCase() + backgroundType.slice(1)}.png`;
        
        const bgImage = await loadImage(bgImagePath);
        backgroundImageRef.current = bgImage;
        setBackgroundLoaded(true);
        
        letterImagesRef.current = {};
        
        const charMapping = CHAR_MAPPINGS[prefixFormat === PREFIX_FORMATS.NEW ? 'new' : 'old'];
        const letterPromises = Object.entries(charMapping).map(async ([char, fileName]) => {
          try {
            const letterPath = `/${formatFolder}/${fileName}`;
            const img = await loadImage(letterPath);
            letterImagesRef.current[char] = img;
          } catch (err) {
            console.warn(`Could not load ${fileName} for character ${char}`);
          }
        });
        
        await Promise.all(letterPromises);
        setLettersLoaded(true);
      } catch (err) {
        console.error('Image loading error:', err);
        setError('Failed to load images. Please check your image paths and try again.');
      }
    };
    loadImages();
  }, [backgroundType, prefixFormat]);

  // Pixel replacement function for custom colors
  const replacePixelColors = (imageData, colorMap) => {
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      if (a === 0) continue;
      
      for (const [defaultColor, newColor] of Object.entries(colorMap)) {
        const dr = parseInt(defaultColor.slice(1, 3), 16);
        const dg = parseInt(defaultColor.slice(3, 5), 16);
        const db = parseInt(defaultColor.slice(5, 7), 16);
        
        if (Math.abs(r - dr) < 5 && Math.abs(g - dg) < 5 && Math.abs(b - db) < 5) {
          const nr = parseInt(newColor.slice(1, 3), 16);
          const ng = parseInt(newColor.slice(3, 5), 16);
          const nb = parseInt(newColor.slice(5, 7), 16);
          
          data[i] = nr;
          data[i + 1] = ng;
          data[i + 2] = nb;
          break;
        }
      }
    }
    
    return imageData;
  };

  // Manual HSL adjustment for mobile fallback
  const applyHSLAdjustment = (imageData, h, s, l, c) => {
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      
      let r = data[i] / 255;
      let g = data[i + 1] / 255;
      let b = data[i + 2] / 255;
      
      // Convert to HSL
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let hh, ss, ll = (max + min) / 2;
      
      if (max === min) {
        hh = ss = 0;
      } else {
        const d = max - min;
        ss = ll > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: hh = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: hh = ((b - r) / d + 2) / 6; break;
          case b: hh = ((r - g) / d + 4) / 6; break;
        }
      }
      
      // Apply adjustments
      hh = (hh * 360 + h) % 360 / 360;
      ss = Math.max(0, Math.min(1, ss * (s / 100)));
      ll = Math.max(0, Math.min(1, ll * (l / 100)));
      
      // Convert back to RGB
      let nr, ng, nb;
      if (ss === 0) {
        nr = ng = nb = ll;
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1/6) return p + (q - p) * 6 * t;
          if (t < 1/2) return q;
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
          return p;
        };
        
        const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
        const p = 2 * ll - q;
        nr = hue2rgb(p, q, hh + 1/3);
        ng = hue2rgb(p, q, hh);
        nb = hue2rgb(p, q, hh - 1/3);
      }
      
      // Apply contrast
      const contrastFactor = (c / 100);
      nr = (nr - 0.5) * contrastFactor + 0.5;
      ng = (ng - 0.5) * contrastFactor + 0.5;
      nb = (nb - 0.5) * contrastFactor + 0.5;
      
      data[i] = Math.round(Math.max(0, Math.min(255, nr * 255)));
      data[i + 1] = Math.round(Math.max(0, Math.min(255, ng * 255)));
      data[i + 2] = Math.round(Math.max(0, Math.min(255, nb * 255)));
    }
    
    return imageData;
  };

  // Draw main canvas
  const updateCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !backgroundImageRef.current || !lettersLoaded || !backgroundLoaded) return;
    
    try {
      const ctx = canvas.getContext('2d');
      const bgImage = backgroundImageRef.current;
      
      const startX = prefixFormat === PREFIX_FORMATS.NEW ? 3 : 4;
      // Move text down by 1 pixel for new format
      const startY = prefixFormat === PREFIX_FORMATS.NEW ? 4 : 2; 
      const endPadding = prefixFormat === PREFIX_FORMATS.NEW ? 3 : 4;

      // Use custom padding - corrected for old format
      const customStartX = prefixFormat === PREFIX_FORMATS.OLD ? 
        startX + paddingLeft - 2 :  // For old format: adjust base by padding
        startX - 1 + paddingLeft;    // Keep new format as is
        
      const customEndPadding = prefixFormat === PREFIX_FORMATS.OLD ?
        endPadding + paddingRight - 2 :  // For old format: adjust base by padding
        endPadding - 1 + paddingRight;    // Keep new format as is
      
      let currentX = customStartX;
      const processedText = prefixFormat === PREFIX_FORMATS.OLD ? text.toUpperCase() : text;
      const letterWidths = processedText.split('').map(char => {
        const letterImage = letterImagesRef.current[char];
        return letterImage ? letterImage.width : 0;
      });
      
      const totalWidth = letterWidths.length > 0
        ? customStartX + letterWidths.reduce((a, b) => a + b, 0) + customEndPadding
        : 0;
      
      const canvasHeight = prefixFormat === PREFIX_FORMATS.NEW ? 16 : 14;
      
      canvas.width = totalWidth;
      canvas.height = canvasHeight;
      setImageSize({ width: totalWidth, height: canvasHeight });
      
      if (totalWidth === 0) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = false;
      
      // Create offscreen canvas for compositing
      const offCanvas = document.createElement('canvas');
      offCanvas.width = totalWidth;
      offCanvas.height = canvasHeight;
      const offCtx = offCanvas.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      
      // Draw background with proper scaling for 16px height (new format)
      offCtx.drawImage(bgImage, 0, 0, 4, bgImage.height, 0, 0, 4, canvasHeight);
      offCtx.drawImage(bgImage, 4, 0, bgImage.width - 8, bgImage.height, 4, 0, totalWidth - 8, canvasHeight);
      offCtx.drawImage(bgImage, bgImage.width - 4, 0, 4, bgImage.height, totalWidth - 4, 0, 4, canvasHeight);
      
      // Draw letters
      currentX = customStartX;
      processedText.split('').forEach((char) => {
        const letterImage = letterImagesRef.current[char];
        if (letterImage) {
          offCtx.drawImage(letterImage, currentX, startY);
          currentX += letterImage.width;
        }
      });
      
      // Apply color adjustments based on mode
      if (colorMode === COLOR_MODES.DEFAULT) {
        // Try CSS filter first (desktop), fallback to manual pixel manipulation (mobile)
        const testFilter = 'hue-rotate(0deg)';
        ctx.filter = testFilter;
        
        if (ctx.filter === testFilter || ctx.filter !== '') {
          // CSS filters supported
          ctx.filter = `hue-rotate(${hue}deg) saturate(${saturation}%) brightness(${lightness}%) contrast(${contrast}%)`;
          ctx.drawImage(offCanvas, 0, 0);
        } else {
          // Fallback for mobile
          const imageData = offCtx.getImageData(0, 0, totalWidth, canvasHeight);
          const adjusted = applyHSLAdjustment(imageData, hue, saturation, lightness, contrast);
          ctx.putImageData(adjusted, 0, 0);
        }
      } else if (colorMode === COLOR_MODES.MANUAL) {
        // Get image data and replace colors
        const imageData = offCtx.getImageData(0, 0, totalWidth, canvasHeight);
        
        let colorMap;
        if (prefixFormat === PREFIX_FORMATS.OLD) {
          colorMap = {
            [DEFAULT_COLORS_OLD.font]: fontColor,
            [DEFAULT_COLORS_OLD.shadow]: shadowColor,
            [DEFAULT_COLORS_OLD.outline]: outlineColor,
            [DEFAULT_COLORS_OLD.background]: backgroundColor,
            [DEFAULT_COLORS_OLD.backgroundLight]: backgroundLightColor
          };
        } else {
          // New format color mapping
          colorMap = {
            [DEFAULT_COLORS_NEW.font]: fontColorNew,
            [DEFAULT_COLORS_NEW.shadowTop]: shadowTopNew,
            [DEFAULT_COLORS_NEW.shadowBottom]: shadowBottomNew,
            [DEFAULT_COLORS_NEW.outline]: outlineColorNew,
            [DEFAULT_COLORS_NEW.innerOutlineTop]: innerOutlineTopNew,
            [DEFAULT_COLORS_NEW.innerOutlineBottom]: innerOutlineBottomNew,
            [DEFAULT_COLORS_NEW.backgroundTop]: backgroundTopNew,
            [DEFAULT_COLORS_NEW.backgroundBottom]: backgroundBottomNew
          };
        }
        
        const replaced = replacePixelColors(imageData, colorMap);
        ctx.putImageData(replaced, 0, 0);
      }
    } catch (err) {
      console.error('Canvas update error:', err);
      setError('Failed to update preview. Please try again.');
    }
  };

  useEffect(() => {
    const updateTimer = setTimeout(() => {
      if (backgroundLoaded && lettersLoaded) {
        requestAnimationFrame(() => {
          updateCanvas();
        });
      }
    }, 10);
    
    return () => clearTimeout(updateTimer);
  }, [
    backgroundLoaded,
    lettersLoaded,
    text,
    hue,
    saturation,
    lightness,
    contrast,
    prefixFormat,
    colorMode,
    fontColor,
    shadowColor,
    outlineColor,
    backgroundColor,
    backgroundLightColor,
    fontColorNew,
    shadowTopNew,
    shadowBottomNew,
    outlineColorNew,
    innerOutlineTopNew,
    innerOutlineBottomNew,
    backgroundTopNew,
    backgroundBottomNew,
    paddingLeft,
    paddingRight
  ]);

  // Render preview for examples - updated to use default padding
  const renderPreviewDataURL = (label, settings, currentFormat) => {
    if (!backgroundImageRef.current || !letterImagesRef.current) return null;
    
    const bgImage = backgroundImageRef.current;
    const processedText = currentFormat === PREFIX_FORMATS.OLD ? 
      settings.label.toUpperCase() : 
      settings.label;
    
    // Use the default padding values for examples
    let exampleStartX, exampleEndPadding;
    if (currentFormat === PREFIX_FORMATS.OLD) {
      exampleStartX = 4 + 2 - 2; // base + default padding (2) - 2 = 4
      exampleEndPadding = 4 + 2 - 2; // base + default padding (2) - 2 = 4
    } else {
      exampleStartX = 3 - 1 + 4; // base - 1 + default padding (4)
      exampleEndPadding = 3 - 1 + 3; // base - 1 + default padding (3)
    }
    
    const startY = currentFormat === PREFIX_FORMATS.NEW ? 4 : 2;
    
    const letterWidths = processedText.split('').map((ch) => {
      const img = letterImagesRef.current[ch];
      return img ? img.width : 0;
    });
    
    const totalWidth = letterWidths.length > 0 ? 
      exampleStartX + letterWidths.reduce((a,b)=>a+b,0) + exampleEndPadding : 0;
    if (totalWidth === 0) return null;
    
    const canvasHeight = currentFormat === PREFIX_FORMATS.NEW ? 16 : 14;
    
    const off = document.createElement('canvas');
    off.width = totalWidth;
    off.height = canvasHeight;
    const ctx = off.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    
    // Create temporary canvas for HSL adjustment
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = totalWidth;
    tempCanvas.height = canvasHeight;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.imageSmoothingEnabled = false;
    
    // Draw background
    tempCtx.drawImage(bgImage, 0, 0, 4, bgImage.height, 0, 0, 4, canvasHeight);
    tempCtx.drawImage(bgImage, 4, 0, bgImage.width - 8, bgImage.height, 4, 0, totalWidth - 8, canvasHeight);
    tempCtx.drawImage(bgImage, bgImage.width - 4, 0, 4, bgImage.height, totalWidth - 4, 0, 4, canvasHeight);
    
    // Draw letters
    let cur = exampleStartX;
    processedText.split('').forEach((ch) => {
      const li = letterImagesRef.current[ch];
      if (li) {
        tempCtx.drawImage(li, cur, startY);
        cur += li.width;
      }
    });
    
    // Apply HSL adjustment manually for consistent results
    const imageData = tempCtx.getImageData(0, 0, totalWidth, canvasHeight);
    const adjusted = applyHSLAdjustment(imageData, settings.hue, settings.saturation, settings.lightness, 100);
    ctx.putImageData(adjusted, 0, 0);
    
    return off.toDataURL('image/png');
  };

  useEffect(() => {
    if (!backgroundLoaded || !lettersLoaded) return;
    const previews = {};
    EXAMPLES.forEach((ex) => {
      const dataURL = renderPreviewDataURL(ex.label, ex, prefixFormat);
      if (dataURL) previews[ex.label] = dataURL;
    });
    setExamplePreviews(previews);
  }, [backgroundLoaded, lettersLoaded, backgroundType, prefixFormat]);

  const applyExample = (ex) => {
    // Keep current format when clicking examples
    setText(prefixFormat === PREFIX_FORMATS.OLD ? ex.label.toUpperCase() : ex.label);
    // Preserve current background style unless the example explicitly specifies one
    if (ex.backgroundType != null) {
      setBackgroundType(ex.backgroundType);
    }
    setColorMode(COLOR_MODES.DEFAULT);
    setHue(ex.hue);
    setSaturation(ex.saturation);
    setLightness(ex.lightness);
  };

  const handleExport = () => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return;
    
    try {
      const link = document.createElement('a');
      link.download = `rank-prefix-${text.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      setError('Failed to export image. Please try again.');
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0c2232' }}>
      {/* Navigation Bar */}
      {/* <nav className="border-b border-[#2a2a2a]" style={{ backgroundColor: '#1d1d1d' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <a
                href="https://nitrosetups.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
                aria-label="NitroSetups"
              >
                <img
                  src="https://nitrosetups.com/_assets/media/c7da482a38258db33a27bf78436c5854.png"
                  alt="NitroSetups"
                  className="h-9 w-auto"
                />
              </a>
            </div>
            <div className="flex space-x-8">
              <a
                href="https://discord.gg/tGECCARXqj"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#d9d9d9] hover:text-white transition-colors font-semibold"
              >
                Discord
              </a>
              <a
                href="https://builtbybit.com/members/chriz745.222259/#resources"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#d9d9d9] hover:text-white transition-colors font-semibold"
              >
                BuiltByBit
              </a>
            </div>
          </div>
        </div>
      </nav> */}

      {/* Title Banner */}
      <div style={{ backgroundColor: '#f8a520' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-extrabold text-[#242424] text-center">
            Minecraft Prefix Creator
          </h1>
        </div>
      </div>

      {/* Title Banner */}
      {/* <div style={{ backgroundColor: '#1d1d1d' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-extrabold text-[#d9d9d9] text-center">
            Minecraft Prefix Creator
          </h1>
        </div>
      </div> */}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Controls Panel */}
          <div className="lg:col-span-1">
            <div className="border border-[#cf871c] rounded-lg p-6" style={{ backgroundColor: '#070f17' }}>
              <h2 className="text-xl font-bold text-white mb-6">Prefix Settings</h2>
              
              {error && (
                <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Prefix Format Section */}
                <div>
                  <label className="block text-[#d9d9d9] text-sm mb-2 font-semibold">Prefix Format</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center justify-center bg-[#242424] border border-[#2a2a2a] rounded px-3 py-2 cursor-pointer hover:border-[#cf871c] transition-colors">
                      <input
                        type="radio"
                        value={PREFIX_FORMATS.NEW}
                        checked={prefixFormat === PREFIX_FORMATS.NEW}
                        onChange={(e) => setPrefixFormat(e.target.value)}
                        className="mr-2 accent-[#cf871c]"
                      />
                      <span className="text-white">New Format</span>
                    </label>
                    <label className="flex items-center justify-center bg-[#242424] border border-[#2a2a2a] rounded px-3 py-2 cursor-pointer hover:border-[#cf871c] transition-colors">
                      <input
                        type="radio"
                        value={PREFIX_FORMATS.OLD}
                        checked={prefixFormat === PREFIX_FORMATS.OLD}
                        onChange={(e) => {
                          setPrefixFormat(PREFIX_FORMATS.OLD);
                          const oldAllowedChars = getAllowedChars(PREFIX_FORMATS.OLD);
                          const filtered = text.toUpperCase().match(oldAllowedChars);
                          setText(filtered ? filtered.join('') : '');
                        }}
                        className="mr-2 accent-[#cf871c]"
                      />
                      <span className="text-white">Old Format</span>
                    </label>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[#d9d9d9] text-sm font-semibold">Prefix Text</label>
                    <button
                      onClick={() => setShowSymbolsModal(true)}
                      className="text-[#cf871c] hover:text-[#ffb43d] transition-colors"
                      title="View available symbols"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    type="text"
                    value={text}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder={getPlaceholder()}
                    className="w-full bg-[#242424] border border-[#2a2a2a] rounded px-3 py-2 text-white placeholder-gray-500 focus:border-[#cf871c] outline-none"
                    maxLength={14}
                  />
                  <p className="text-xs text-[#999] mt-1">
                    Max 14 characters
                  </p>
                </div>

                <div>
                  <label className="block text-[#d9d9d9] text-sm mb-2 font-semibold">Background Style</label>
                  <select 
                    value={backgroundType}
                    onChange={(e) => setBackgroundType(e.target.value)}
                    className="w-full bg-[#242424] border border-[#2a2a2a] rounded px-3 py-2 text-white focus:border-[#cf871c] outline-none"
                  >
                    <option value="straight">Straight</option>
                    <option value="rounded">Rounded</option>
                  </select>
                </div>

                {/* Padding Controls */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[#d9d9d9] text-sm font-semibold">Background Padding</label>
                    <button
                      onClick={resetPadding}
                      className="text-xs px-2 py-1 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[#999] text-xs mb-1">Left ({paddingLeft}px)</label>
                      <input
                        type="range"
                        min={getMinPadding()}
                        max="12"
                        value={paddingLeft}
                        onChange={(e) => setPaddingLeft(parseInt(e.target.value))}
                        className="w-full h-2 bg-[#2a2a2a] rounded-lg appearance-none cursor-pointer slider-orange"
                        style={{
                          background: `linear-gradient(to right, #ff8c00 0%, #ff8c00 ${((paddingLeft - getMinPadding()) / (12 - getMinPadding())) * 100}%, #2a2a2a ${((paddingLeft - getMinPadding()) / (12 - getMinPadding())) * 100}%, #2a2a2a 100%)`
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-[#999] text-xs mb-1">Right ({paddingRight}px)</label>
                      <input
                        type="range"
                        min={getMinPadding()}
                        max="12"
                        value={paddingRight}
                        onChange={(e) => setPaddingRight(parseInt(e.target.value))}
                        className="w-full h-2 bg-[#2a2a2a] rounded-lg appearance-none cursor-pointer slider-orange"
                        style={{
                          background: `linear-gradient(to right, #ff8c00 0%, #ff8c00 ${((paddingRight - getMinPadding()) / (12 - getMinPadding())) * 100}%, #2a2a2a ${((paddingRight - getMinPadding()) / (12 - getMinPadding())) * 100}%, #2a2a2a 100%)`
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#2a2a2a]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">Color Adjustments</h3>
                    <button
                      onClick={() => {
                        setHue(0);
                        setSaturation(100);
                        setLightness(100);
                        setContrast(100);
                        setFontColor(DEFAULT_COLORS_OLD.font);
                        setShadowColor(DEFAULT_COLORS_OLD.shadow);
                        setOutlineColor(DEFAULT_COLORS_OLD.outline);
                        setBackgroundColor(DEFAULT_COLORS_OLD.background);
                        setBackgroundLightColor(DEFAULT_COLORS_OLD.backgroundLight);
                        setFontColorNew(DEFAULT_COLORS_NEW.font);
                        setShadowTopNew(DEFAULT_COLORS_NEW.shadowTop);
                        setShadowBottomNew(DEFAULT_COLORS_NEW.shadowBottom);
                        setOutlineColorNew(DEFAULT_COLORS_NEW.outline);
                        setInnerOutlineTopNew(DEFAULT_COLORS_NEW.innerOutlineTop);
                        setInnerOutlineBottomNew(DEFAULT_COLORS_NEW.innerOutlineBottom);
                        setBackgroundTopNew(DEFAULT_COLORS_NEW.backgroundTop);
                        setBackgroundBottomNew(DEFAULT_COLORS_NEW.backgroundBottom);
                      }}
                      className="px-3 py-1 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded text-sm transition-colors"
                    >
                      Reset Colors
                    </button>
                  </div>

                  {/* Color Mode Tabs */}
                  <div className="mb-4">
                    <div className="flex gap-2 bg-[#242424] p-1 rounded-lg">
                      <button
                        onClick={() => setColorMode(COLOR_MODES.DEFAULT)}
                        className={`flex-1 px-3 py-2 rounded text-sm font-semibold transition-colors ${
                          colorMode === COLOR_MODES.DEFAULT
                            ? 'bg-[#cf871c] text-white'
                            : 'bg-[#2a2a2a] text-[#999] hover:text-white'
                        }`}
                      >
                        Default
                      </button>
                      <button
                        onClick={() => setColorMode(COLOR_MODES.MANUAL)}
                        className={`flex-1 px-3 py-2 rounded text-sm font-semibold transition-colors ${
                          colorMode === COLOR_MODES.MANUAL
                            ? 'bg-[#cf871c] text-white'
                            : 'bg-[#2a2a2a] text-[#999] hover:text-white'
                        }`}
                      >
                        Manual
                      </button>
                    </div>
                  </div>

                  {/* Color Controls based on mode */}
                  {colorMode === COLOR_MODES.DEFAULT && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Slider label="Hue" value={hue} min={-180} max={180} onChange={setHue} />
                        <Slider label="Saturation" value={saturation} min={0} max={200} onChange={setSaturation} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Slider label="Contrast" value={contrast} min={0} max={200} onChange={setContrast} />
                        <Slider label="Lightness" value={lightness} min={0} max={200} onChange={setLightness} />
                      </div>
                    </div>
                  )}

                  {colorMode === COLOR_MODES.MANUAL && (
                    <>
                      {prefixFormat === PREFIX_FORMATS.OLD ? (
                        <div className="grid grid-cols-2 gap-3">
                          <ColorPickerSimple label="Font" value={fontColor} onChange={setFontColor} />
                          <ColorPickerSimple label="Shadow" value={shadowColor} onChange={setShadowColor} />
                          <ColorPickerSimple label="Outline" value={outlineColor} onChange={setOutlineColor} />
                          <ColorPickerSimple label="Background" value={backgroundColor} onChange={setBackgroundColor} />
                          <ColorPickerSimple label="Background Light" value={backgroundLightColor} onChange={setBackgroundLightColor} />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <ColorPickerSimple label="Font Color" value={fontColorNew} onChange={setFontColorNew} />
                          <ColorPickerSimple label="Shadow Top" value={shadowTopNew} onChange={setShadowTopNew} />
                          <ColorPickerSimple label="Shadow Bottom" value={shadowBottomNew} onChange={setShadowBottomNew} />
                          <ColorPickerSimple label="Outline" value={outlineColorNew} onChange={setOutlineColorNew} />
                          <ColorPickerSimple label="Inner Outline Top" value={innerOutlineTopNew} onChange={setInnerOutlineTopNew} />
                          <ColorPickerSimple label="Inner Outline Bottom" value={innerOutlineBottomNew} onChange={setInnerOutlineBottomNew} />
                          <ColorPickerSimple label="Background Top" value={backgroundTopNew} onChange={setBackgroundTopNew} />
                          <ColorPickerSimple label="Background Bottom" value={backgroundBottomNew} onChange={setBackgroundBottomNew} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Prefix Preview Panel */}
          <div className="lg:col-span-2 h-full">
            <div
              className="border border-[#cf871c] rounded-lg p-5 h-full flex flex-col"
              style={{ backgroundColor: '#070f17' }}
            >
              <h2 className="text-xl font-bold text-white mb-3">Prefix Preview</h2>
              
              {/* Live Preview */}
              <div className="bg-[#242424] rounded-lg px-6 py-6 flex justify-center items-center min-h-[180px]">
                {(!backgroundLoaded || !lettersLoaded) ? (
                  <div className="text-[#999]">Loading...</div>
                ) : text.length === 0 ? (
                  <div className="text-[#999]">Enter text to see preview</div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <canvas
                      ref={canvasRef}
                      className="pixelated"
                      style={{
                        imageRendering: 'pixelated',
                        transform: 'scale(7)',
                        transformOrigin: 'center'
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div className="text-xs text-[#999] mt-2 text-center">
                Size: {imageSize.width}x{imageSize.height} px
              </div>

              {/* Examples */}
              <div className="mt-4">
                <p className="text-xs text-[#d9d9d9] mb-2">Examples (click to apply):</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex.label}
                      onClick={() => applyExample(ex)}
                      className="group bg-[#242424] border border-[#2a2a2a] rounded-lg px-3 py-2 flex flex-col items-center hover:border-[#cf871c] transition-colors"
                      title={`Apply ${ex.label}`}
                    >
                      <div className="flex items-center justify-center w-full min-h-[110px]">
                        {examplePreviews[ex.label] ? (
                          <img
                            src={examplePreviews[ex.label]}
                            alt={`${ex.label} preview`}
                            className="block pixelated"
                            style={{
                              imageRendering: 'pixelated',
                              transform: 'scale(3)',
                              transformOrigin: 'center'
                            }}
                          />
                        ) : (
                          <span className="text-[#999] text-xs">Loading…</span>
                        )}
                      </div>
                      <span className="text-white font-semibold mt-2 leading-tight">{ex.label}</span>
                      <span className="text-[#999] text-[11px] mt-0.5">
                        {ex.label === 'Member' ? 'Hue: 0°, Saturation: 0%' : `Hue: ${ex.hue}°`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-[#242424] rounded-lg border-l-4 border-[#cf871c]">
                <p className="text-sm text-[#d9d9d9]">
                  <span className="font-semibold text-white">
                    {prefixFormat === PREFIX_FORMATS.NEW ? 'New Format:' : 'Old Format:'}
                  </span>{' '}
                  {prefixFormat === PREFIX_FORMATS.NEW
                    ? '16px text height. Supports lowercase, numbers, and extended symbols.'
                    : '14px text height. Uppercase only, numbers, and basic punctuation.'}
                </p>
                <p className="text-sm text-[#d9d9d9] mt-3 pt-3 border-t border-[#2a2a2a]">
                  <span className="font-semibold text-white">
                    {colorMode === COLOR_MODES.DEFAULT ? 'Default Mode:' : 'Manual Mode:'}
                  </span>{' '}
                  {colorMode === COLOR_MODES.DEFAULT 
                    ? 'Adjust hue, saturation, contrast, and lightness for the entire prefix.'
                    : 'Set each color channel independently for full control.'}
                </p>
              </div>

              {/* Actions */}
              <div className="mt-auto pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={handleExport}
                  disabled={!backgroundLoaded || !lettersLoaded || !text}
                  className={`w-full py-2 rounded font-semibold transition-colors ${
                    (!backgroundLoaded || !lettersLoaded || !text)
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-[#cf871c] hover:bg-[#b87a1a]'
                  }`}
                >
                  {(!backgroundLoaded || !lettersLoaded) ? 'Loading…' : 'Export as PNG'}
                </button>
                
                {/* Updated Generate Config button with dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowConfigDropdown(!showConfigDropdown)}
                    disabled={!text}
                    className={`w-full py-2 px-4 rounded transition-colors flex items-center justify-center relative ${
                      !text
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'
                    }`}
                  >
                    <span>Generate Config</span>
                    <ChevronDown className={`w-4 h-4 transition-transform absolute right-4 ${showConfigDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showConfigDropdown && !(!text) && (
                    <div className="absolute bottom-full mb-1 left-0 right-0 bg-[#242424] border border-[#2a2a2a] rounded-md shadow-lg overflow-hidden">
                      <button
                        onClick={() => {
                          setConfigFormat('vanilla');
                          setShowConfigDropdown(false);
                          handleGenerateJson('vanilla');  // Pass format directly
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-[#3a3a3a] transition-colors ${configFormat === 'vanilla' ? 'bg-[#3a3a3a]' : ''}`}
                      >
                        Vanilla JSON
                      </button>
                      <button
                        onClick={() => {
                          setConfigFormat('nexo');
                          setShowConfigDropdown(false);
                          handleGenerateJson('nexo');  // Pass format directly
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-[#3a3a3a] transition-colors ${configFormat === 'nexo' ? 'bg-[#3a3a3a]' : ''}`}
                      >
                        Nexo Config
                      </button>
                      <button
                        onClick={() => {
                          setConfigFormat('itemsadder');
                          setShowConfigDropdown(false);
                          handleGenerateJson('itemsadder');  // Pass format directly
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-[#3a3a3a] transition-colors ${configFormat === 'itemsadder' ? 'bg-[#3a3a3a]' : ''}`}
                      >
                        ItemsAdder Config
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* JSON Modal */}
      {showJsonModal && jsonCode && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#242424] rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  {configFormat === 'vanilla' ? 'Vanilla Resource Pack JSON' : 
                  configFormat === 'nexo' ? (
                    <>Nexo Configuration <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded font-medium">BETA</span></>
                  ) : (
                    <>ItemsAdder Configuration <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded font-medium">BETA</span></>
                  )}
                </h3>
                <button 
                  onClick={() => setShowJsonModal(false)}
                  className="text-[#999] hover:text-white transition-colors text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-[#070f17] rounded-lg border border-[#2a2a2a]">
                <p className="text-sm text-[#d9d9d9]">
                  {configFormat === 'vanilla' && (
                    <>Place this JSON in your resource pack at: <br/>
                    <code className="text-[#cf871c]">assets/minecraft/font/default.json</code></>
                  )}
                  {configFormat === 'nexo' && (
                    <>Place this configuration in: <br/>
                    <code className="text-[#cf871c]">plugins/Nexo/glyphs/prefixes.yml</code></>
                  )}
                  {configFormat === 'itemsadder' && (
                    <>Place this configuration in: <br/>
                    <code className="text-[#cf871c]">plugins/ItemsAdder/contents/[namespace]/configs/prefixes.yml</code></>
                  )}
                </p>
                <p className="text-sm text-[#999] mt-2">
                  Don't forget to place the exported PNG image in the appropriate textures folder!
                </p>
              </div>
              
              <pre className="bg-[#070f17] text-[#cf871c] p-4 rounded-lg text-sm overflow-x-auto font-mono">
                {jsonCode}
              </pre>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(jsonCode);
                  alert('Configuration copied to clipboard!');
                }}
                className="mt-4 w-full py-2 bg-[#cf871c] hover:bg-[#b87a1a] rounded font-semibold transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Symbols Modal */}
      {showSymbolsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#242424] rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Available Symbols</h3>
                <button 
                  onClick={() => setShowSymbolsModal(false)}
                  className="text-[#999] hover:text-white transition-colors text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-[#cf871c] font-semibold mb-2">New Format (16px height)</h4>
                  <div className="bg-[#070f17] p-3 rounded">
                    <p className="text-[#d9d9d9] text-sm mb-2">Letters: A-Z, a-z</p>
                    <p className="text-[#d9d9d9] text-sm mb-2">Numbers: 0-9</p>
                    <p className="text-[#d9d9d9] text-sm">
                      Symbols: <span className="font-mono text-white">- . ! ? : # % + / \ [ ] ( ) { } " ' | space</span>
                    </p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-[#cf871c] font-semibold mb-2">Old Format (14px height)</h4>
                  <div className="bg-[#070f17] p-3 rounded">
                    <p className="text-[#d9d9d9] text-sm mb-2">Letters: A-Z (uppercase only)</p>
                    <p className="text-[#d9d9d9] text-sm mb-2">Numbers: 0-9</p>
                    <p className="text-[#d9d9d9] text-sm">
                      Symbols: <span className="font-mono text-white">- . space</span>
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setShowSymbolsModal(false)}
                className="mt-4 w-full py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-[#2a2a2a] mt-3" style={{ backgroundColor: '#060f16' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-col sm:flex-row">
            {/* Left: Kaludi credit */}
            <div className="flex items-center gap-4">
              <img 
                src="/images/Kaludi Logo.png"
                alt="Kaludi"
                className="h-12 w-auto opacity-90"
              />
              <p className="text-[#bbb] text-sm">
                Created by <a href="https://github.com/Kaludii" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Kaludi</a>.{' '}
                Image template © <a href="https://nitrosetups.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">NitroSetups</a>.{' '}
                Not affiliated with Mojang or Microsoft.
              </p>
            </div>

            {/* Right: NitroSetups logo + links */}
            <div className="flex items-center gap-12 pt-4 sm:pt-0">
              <a
                href="https://nitrosetups.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="NitroSetups"
                className="inline-flex"
                title="NitroSetups"
              >
                <img
                  src="https://nitrosetups.com/_assets/media/c7da482a38258db33a27bf78436c5854.png"
                  alt="NitroSetups"
                  className="h-8 w-auto opacity-90"
                />
              </a>

              {/* keep wider space between the two links */}
              <div className="flex items-center space-x-6">
                <a
                  href="https://discord.gg/tGECCARXqj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#999] hover:text-[#cf871c] text-sm transition-colors font-semibold"
                >
                  Discord
                </a>
                <a
                  href="https://builtbybit.com/members/chriz745.222259/#resources"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#999] hover:text-[#cf871c] text-sm transition-colors font-semibold"
                >
                  BuiltByBit
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>



      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #cf871c;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #070f17;
        }
        
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #cf871c;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #070f17;
        }

        .slider-orange::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #ff8c00;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #070f17;
        }
        
        .slider-orange::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #ff8c00;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #070f17;
        }
        
        .pixelated {
          image-rendering: -moz-crisp-edges;
          image-rendering: -webkit-crisp-edges;
          image-rendering: pixelated;
          image-rendering: crisp-edges;
        }
      `}</style>
    </div>
  );
};

export default NitroSetupsPrefixCreator;